import { useEffect, useState } from 'react';
import { isEqual } from 'lodash';
import { noTeamOption } from 'constants/components/teamSelect';
import { UseTeamsMenuSelectionArgs } from './types';

export const useTeamsMenuSelection = ({
  teamsSelected,
  withSubmit,
  onTeamSelect,
  onClose,
  teamRemoved,
}: UseTeamsMenuSelectionArgs) => {
  const [selected, setSelected] = useState<string[]>([]);
  const [prevSelected, setPrevSelected] = useState<string[]>([]);

  const handleTeamSelection = (id: string) => {
    const currentSelected = selected;
    setSelected((prevState) => {
      if (id === noTeamOption && !prevState.length) {
        return prevState;
      }
      if (id === noTeamOption) {
        return [];
      }
      if (prevState.includes(id)) {
        return prevState.filter((ps) => ps !== id);
      } else {
        return [...prevState, id];
      }
    });
    setPrevSelected(currentSelected);
  };

  const handleClose = () => {
    setSelected(prevSelected);
    onClose();
  };

  const handleTeamChangeSubmit = () => {
    onTeamSelect(selected);
    setPrevSelected(selected);
    handleClose();
  };

  useEffect(() => {
    if (teamsSelected && teamsSelected.length > 0) {
      // Resolve the id from either `team_id` (most callers) or the nested
      // `team.id` (TeamSelector/TeamsFilter build their selected objects
      // with `id` and no `team_id`). Reading only `team_id` left those
      // callers with `undefined` ids, which corrupted the selection on the
      // second change — empty pills and phantom `,,` entries in filters.
      // `filter(Boolean)` drops any entry that resolves to neither.
      const currentTeams = teamsSelected
        .map((team) => team.team_id ?? team.team?.id)
        .filter(Boolean) as string[];
      setSelected(currentTeams);
      setPrevSelected(currentTeams);
    }
  }, [teamsSelected]);

  useEffect(() => {
    withSubmit && !isEqual(prevSelected, selected) && onTeamSelect(selected);
  }, [selected]);

  useEffect(() => {
    // `teamRemoved` is wrapped in an object so the dep array sees a new
    // reference on every removal — but the wrapped id can still be falsy
    // if the caller doesn't have a real team id, so guard before toggling.
    if (teamRemoved?.teamId) handleTeamSelection(teamRemoved.teamId);
  }, [teamRemoved]);

  return {
    selected,
    handleTeamSelection,
    handleTeamChangeSubmit,
    handleClose,
  };
};
